import { createFileRoute, redirect } from "@tanstack/react-router";
import { arrivingStepIn } from "@/client/components/arriving";
import {
  ArrivingFailed,
  ArrivingLayout,
  ArrivingPending,
} from "@/client/components/arriving-layout";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { sessionQuery } from "@/client/lib/session";
import { EmailStep } from "@/features/identity/components/email-step";
import { PasswordStep } from "@/features/identity/components/password-step";
import {
  LockedStep,
  NotLinked,
  Opening,
  TwoStepStep,
} from "@/features/identity/components/sign-in-states";
import { servedByWorker, signInSearch } from "@/features/identity/search";
import {
  type SignInFlow,
  useSignInFlow,
} from "@/features/identity/use-sign-in-flow";

export const Route = createFileRoute("/sign-in")({
  pendingMs: PENDING_MS,
  pendingComponent: ArrivingPending,
  errorComponent: ArrivingFailed,
  validateSearch: signInSearch,
  // A social sign-in lands here already signed in, so it is sent on to `redirect`, which the
  // search schema has already vetted.
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (session.signedIn) {
      throw redirect({
        href: search.redirect,
        reloadDocument: servedByWorker(search.redirect),
      });
    }
  },
  head: async () => {
    const { identityCopy } = await import("@/features/identity/copy");
    return { meta: [{ title: pageTitle(identityCopy.tab) }] };
  },
  component: SignIn,
});

function SignIn() {
  const { redirect: destination, email, problem, error } = Route.useSearch();
  const notLinked = problem === "google" && error === "account_not_linked";

  return (
    <ArrivingLayout>
      {notLinked ? (
        <NotLinked destination={destination} />
      ) : (
        <SignInSteps
          destination={destination}
          email={email}
          googleFailed={problem === "google"}
        />
      )}
    </ArrivingLayout>
  );
}

function SignInSteps({
  destination,
  email,
  googleFailed,
}: {
  readonly destination: string;
  readonly email?: string;
  readonly googleFailed: boolean;
}) {
  const flow = useSignInFlow(destination, email, googleFailed);
  const step = flow.opening !== null ? "opening" : flow.state.step;

  return (
    <div key={step} className={arrivingStepIn}>
      {flow.opening !== null ? (
        <Opening name={flow.opening} />
      ) : (
        <SignInStep flow={flow} />
      )}
    </div>
  );
}

function SignInStep({ flow }: { readonly flow: SignInFlow }) {
  switch (flow.state.step) {
    case "email":
      return <EmailStep flow={flow} />;
    case "password":
      return <PasswordStep flow={flow} />;
    case "two-step":
      return <TwoStepStep flow={flow} />;
    case "locked":
      return <LockedStep flow={flow} />;
  }
}
